import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, Users, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseDate } from '@/lib/localizedDateUtils';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MoneyDisplay } from '@/components/finance';
import { useGenerateMonthlyExpenses, usePreviewMonthlyExpenses } from '@/hooks/finance/useGenerateMonthlyExpenses';

interface GenerateExpensesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

export function GenerateExpensesModal({ open, onOpenChange }: GenerateExpensesModalProps) {
  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [includeRecurring, setIncludeRecurring] = useState(true);
  const [includePayroll, setIncludePayroll] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const generateMutation = useGenerateMonthlyExpenses();
  const previewMutation = usePreviewMonthlyExpenses();

  const years = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() + i - 1);

  useEffect(() => {
    if (open) {
      setShowPreview(false);
      previewMutation.reset();
    }
  }, [open]);

  const handlePreview = async () => {
    try {
      await previewMutation.mutateAsync({ month, year, includeRecurring, includePayroll });
      setShowPreview(true);
    } catch (error) {
      toast.error('Erro ao carregar preview');
      console.error(error);
    }
  };

  const handleGenerate = async () => {
    if (!includeRecurring && !includePayroll) {
      toast.error('Selecione ao menos um tipo de lançamento');
      return;
    }

    try {
      const result = await generateMutation.mutateAsync({
        month,
        year,
        includeRecurring,
        includePayroll,
      });

      if (result.inserted === 0 && result.skipped > 0) {
        toast.info(`Nenhum lançamento criado. ${result.skipped} já existiam.`);
      } else if (result.inserted > 0) {
        toast.success(`${result.inserted} lançamentos criados com sucesso!${result.skipped > 0 ? ` (${result.skipped} já existiam)` : ''}`);
        onOpenChange(false);
      } else {
        toast.info('Nenhum lançamento para gerar. Verifique se há despesas recorrentes ou funcionários cadastrados.');
      }
    } catch (error) {
      toast.error('Erro ao gerar lançamentos');
      console.error(error);
    }
  };

  const previewData = previewMutation.data || [];
  const totalUSD = previewData.filter(p => p.currency === 'USD').reduce((sum, p) => sum + p.amount, 0);
  const totalBRL = previewData.filter(p => p.currency === 'BRL').reduce((sum, p) => sum + p.amount, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Gerar Lançamentos do Mês
          </DialogTitle>
          <DialogDescription>
            Crie lançamentos no Contas a Pagar a partir de despesas recorrentes e folha de pagamento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 flex-1 overflow-hidden flex flex-col">
          {/* Period Selection */}
          <div className="flex gap-4">
            <div className="space-y-2 flex-1">
              <Label>Mês</Label>
              <Select value={month.toString()} onValueChange={(v) => { setMonth(parseInt(v)); setShowPreview(false); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <Label>Ano</Label>
              <Select value={year.toString()} onValueChange={(v) => { setYear(parseInt(v)); setShowPreview(false); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Type Selection */}
          <div className="space-y-3">
            <Label>Tipos de Lançamento</Label>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="recurring"
                  checked={includeRecurring}
                  onCheckedChange={(checked) => { setIncludeRecurring(!!checked); setShowPreview(false); }}
                />
                <Label htmlFor="recurring" className="flex items-center gap-2 cursor-pointer font-normal">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  Despesas Recorrentes
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="payroll"
                  checked={includePayroll}
                  onCheckedChange={(checked) => { setIncludePayroll(!!checked); setShowPreview(false); }}
                />
                <Label htmlFor="payroll" className="flex items-center gap-2 cursor-pointer font-normal">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Folha de Pagamento
                </Label>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {showPreview && (
            <div className="flex-1 overflow-hidden flex flex-col border rounded-lg">
              <div className="bg-muted/50 px-4 py-2 border-b flex justify-between items-center">
                <span className="text-sm font-medium">
                  Preview: {previewData.length} lançamentos
                </span>
                <div className="flex gap-4 text-sm">
                  {totalUSD > 0 && (
                    <span className="text-muted-foreground">
                      USD: <MoneyDisplay value={totalUSD} currency="USD" />
                    </span>
                  )}
                  {totalBRL > 0 && (
                    <span className="text-muted-foreground">
                      BRL: <MoneyDisplay value={totalBRL} currency="BRL" />
                    </span>
                  )}
                </div>
              </div>
              <ScrollArea className="flex-1 max-h-[300px]">
                {previewData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mb-2" />
                    <p>Nenhum lançamento para gerar</p>
                    <p className="text-xs">Cadastre despesas recorrentes ou funcionários primeiro</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Badge variant={
                              item.type === 'payroll' ? 'default' : 
                              item.type === 'fixed' ? 'secondary' : 
                              'outline'
                            }>
                              {item.type === 'payroll' ? 'Folha' : 
                               item.type === 'fixed' ? '🔒 Fixa' : 
                               '📈 Variável'}
                            </Badge>
                            {item.isEstimate && (
                              <Badge variant="outline" className="ml-1 text-yellow-600 border-yellow-600">
                                ⚠️ Est.
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.description}</p>
                              <p className="text-xs text-muted-foreground">{item.source}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {format(parseDate(item.dueDate) || new Date(), 'dd/MM/yyyy', { locale: ptBR })}
                          </TableCell>
                          <TableCell className="text-right">
                            <MoneyDisplay value={item.amount} currency={item.currency as 'USD' | 'BRL'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          {!showPreview ? (
            <Button 
              onClick={handlePreview} 
              disabled={previewMutation.isPending || (!includeRecurring && !includePayroll)}
            >
              {previewMutation.isPending && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Visualizar
            </Button>
          ) : (
            <Button 
              onClick={handleGenerate} 
              disabled={generateMutation.isPending || previewData.length === 0}
            >
              {generateMutation.isPending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Gerar {previewData.length} Lançamentos
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
